import { useState, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { toast } from '@/hooks/use-toast';
import { Trash2, Plus, Save, Upload, Crop, Loader2, Pencil } from 'lucide-react';
import { MarkdownEditor } from '@/components/forum/MarkdownEditor';
import { uploadBlogImageWithProgress } from '@/lib/blogUploads';
import { ImageCropDialog } from './ImageCropDialog';

async function uploadPartnerLogo(blob: Blob): Promise<string> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('You must be signed in to upload partner logos');
  const path = `${user.id}/partners/${crypto.randomUUID()}.png`;
  const { error } = await supabase.storage.from('agency-logos').upload(path, blob, {
    contentType: 'image/png', upsert: false,
  });
  if (error) throw error;
  return supabase.storage.from('agency-logos').getPublicUrl(path).data.publicUrl;
}

const ICON_OPTIONS = ['Landmark', 'Calculator', 'BarChart3', 'MapPin', 'Building2', 'Home', 'TrendingUp', 'Users', 'Shield', 'ShieldCheck', 'Handshake', 'LandPlot', 'Briefcase', 'FileText', 'Award', 'Newspaper'];

function getEn(field: any): string {
  if (!field) return '';
  if (typeof field === 'string') return field;
  return field.en || Object.values(field)[0] as string || '';
}

// ───── Hero ─────
function HeroEditor() {
  const qc = useQueryClient();
  const { data: hero, isLoading } = useQuery({
    queryKey: ['cms-hero'],
    queryFn: async () => {
      const { data } = await supabase.from('landing_hero').select('*').order('updated_at', { ascending: false }).limit(1).maybeSingle();
      return data;
    },
  });

  const [titleEn, setTitleEn] = useState('');
  const [titleAm, setTitleAm] = useState('');
  const [titleOm, setTitleOm] = useState('');
  const [subtitleEn, setSubtitleEn] = useState('');
  const [bgUrl, setBgUrl] = useState('');
  const [active, setActive] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (hero) {
      const t: any = hero.title || {};
      const s: any = hero.subtitle || {};
      setTitleEn(t.en || '');
      setTitleAm(t.am || '');
      setTitleOm(t.om || '');
      setSubtitleEn(s.en || '');
      setBgUrl(hero.background_url || '');
      setActive(hero.is_active);
    }
  }, [hero]);

  const save = async () => {
    setSaving(true);
    const payload = {
      title: { en: titleEn, am: titleAm, om: titleOm },
      subtitle: subtitleEn ? { en: subtitleEn } : null,
      background_url: bgUrl || null,
      is_active: active,
    };
    const { error } = hero?.id
      ? await supabase.from('landing_hero').update(payload).eq('id', hero.id)
      : await supabase.from('landing_hero').insert(payload);
    setSaving(false);
    if (error) return toast({ title: 'Save failed', description: error.message, variant: 'destructive' });
    toast({ title: 'Hero updated' });
    qc.invalidateQueries({ queryKey: ['cms-hero'] });
    qc.invalidateQueries({ queryKey: ['landing-hero'] });
  };

  if (isLoading) return <p className="text-sm text-muted-foreground">Loading…</p>;

  return (
    <Card>
      <CardHeader><CardTitle>Hero Section</CardTitle></CardHeader>
      <CardContent className="space-y-4">
        <div className="grid md:grid-cols-3 gap-3">
          <div><Label>Title (English)</Label><Input value={titleEn} onChange={e => setTitleEn(e.target.value)} /></div>
          <div><Label>Title (Amharic)</Label><Input value={titleAm} onChange={e => setTitleAm(e.target.value)} /></div>
          <div><Label>Title (Oromo)</Label><Input value={titleOm} onChange={e => setTitleOm(e.target.value)} /></div>
        </div>
        <div><Label>Subtitle (English)</Label><Input value={subtitleEn} onChange={e => setSubtitleEn(e.target.value)} /></div>
        <div><Label>Background image URL (optional)</Label><Input value={bgUrl} onChange={e => setBgUrl(e.target.value)} placeholder="https://…" /></div>
        <div className="flex items-center gap-2"><Switch checked={active} onCheckedChange={setActive} /><Label>Active</Label></div>
        <Button onClick={save} disabled={saving}><Save className="h-4 w-4 mr-2" />{saving ? 'Saving…' : 'Save'}</Button>
      </CardContent>
    </Card>
  );
}

// ───── Generic list editor for news / blog ─────
type Article = { id?: string; title: any; summary?: any; excerpt?: any; cover_url?: string | null; slug?: string | null; published_at?: string; is_published: boolean };

function ArticleEditor({ table, hasSlug }: { table: 'news_articles' | 'blog_posts'; hasSlug: boolean }) {
  const qc = useQueryClient();
  const summaryField = table === 'news_articles' ? 'summary' : 'excerpt';
  const { data: items = [] } = useQuery({
    queryKey: [`cms-${table}`],
    queryFn: async () => {
      const { data } = await supabase.from(table).select('*').order('published_at', { ascending: false });
      return data || [];
    },
  });

  const [draft, setDraft] = useState<any>({ title: '', summaryText: '', body: '', category: '', tagsText: '', author_name: '', cover_url: '', slug: '', is_published: true });

  const reset = () => setDraft({ title: '', summaryText: '', body: '', category: '', tagsText: '', author_name: '', cover_url: '', slug: '', is_published: true });

  const create = async () => {
    if (!draft.title.trim()) return toast({ title: 'Title required', variant: 'destructive' });
    const payload: any = {
      title: { en: draft.title },
      [summaryField]: draft.summaryText ? { en: draft.summaryText } : null,
      cover_url: draft.cover_url || null,
      is_published: draft.is_published,
    };
    if (table === 'blog_posts') {
      payload.body = draft.body ? { en: draft.body } : null;
      payload.category = draft.category || null;
      payload.tags = draft.tagsText
        ? draft.tagsText.split(',').map((t: string) => t.trim()).filter(Boolean)
        : [];
      payload.author_name = draft.author_name || null;
    }
    if (hasSlug) payload.slug = draft.slug || draft.title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
    const { error } = await supabase.from(table).insert(payload);
    if (error) return toast({ title: 'Create failed', description: error.message, variant: 'destructive' });
    toast({ title: 'Created' });
    reset();
    qc.invalidateQueries({ queryKey: [`cms-${table}`] });
    qc.invalidateQueries({ queryKey: [table === 'news_articles' ? 'latest-news' : 'latest-blog'] });
  };

  const togglePublish = async (item: any) => {
    const { error } = await supabase.from(table).update({ is_published: !item.is_published }).eq('id', item.id);
    if (error) return toast({ title: 'Update failed', description: error.message, variant: 'destructive' });
    qc.invalidateQueries({ queryKey: [`cms-${table}`] });
    qc.invalidateQueries({ queryKey: [table === 'news_articles' ? 'latest-news' : 'latest-blog'] });
  };

  const remove = async (id: string) => {
    if (!confirm('Delete this item?')) return;
    const { error } = await supabase.from(table).delete().eq('id', id);
    if (error) return toast({ title: 'Delete failed', description: error.message, variant: 'destructive' });
    toast({ title: 'Deleted' });
    qc.invalidateQueries({ queryKey: [`cms-${table}`] });
    qc.invalidateQueries({ queryKey: [table === 'news_articles' ? 'latest-news' : 'latest-blog'] });
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader><CardTitle>Add new</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <div><Label>Title (English)</Label><Input value={draft.title} onChange={e => setDraft({ ...draft, title: e.target.value })} /></div>
          <div><Label>{table === 'news_articles' ? 'Summary' : 'Excerpt'} (English)</Label><Textarea rows={2} value={draft.summaryText} onChange={e => setDraft({ ...draft, summaryText: e.target.value })} /></div>
          {table === 'blog_posts' && (
            <>
              <div>
                <Label>Body (Markdown supported)</Label>
                <MarkdownEditor
                  value={draft.body}
                  onChange={(v) => setDraft({ ...draft, body: v })}
                  uploader={uploadBlogImageWithProgress}
                  maxLength={20000}
                  minHeight={240}
                  placeholder={'## Heading\n\nWrite your article here…'}
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div><Label>Category</Label><Input value={draft.category} onChange={e => setDraft({ ...draft, category: e.target.value })} placeholder="Buying / Selling / Renting…" /></div>
                <div><Label>Author name</Label><Input value={draft.author_name} onChange={e => setDraft({ ...draft, author_name: e.target.value })} placeholder="Editorial Team" /></div>
              </div>
              <div><Label>Tags (comma separated)</Label><Input value={draft.tagsText} onChange={e => setDraft({ ...draft, tagsText: e.target.value })} placeholder="addis-ababa, mortgage, tips" /></div>
            </>
          )}
          {hasSlug && <div><Label>Slug (auto if blank)</Label><Input value={draft.slug} onChange={e => setDraft({ ...draft, slug: e.target.value })} /></div>}
          <div><Label>Cover image URL</Label><Input value={draft.cover_url} onChange={e => setDraft({ ...draft, cover_url: e.target.value })} placeholder="https://…" /></div>
          <div className="flex items-center gap-2"><Switch checked={draft.is_published} onCheckedChange={v => setDraft({ ...draft, is_published: v })} /><Label>Published</Label></div>
          <Button onClick={create}><Plus className="h-4 w-4 mr-2" /> Create</Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle>Existing ({items.length})</CardTitle></CardHeader>
        <CardContent className="space-y-2">
          {items.length === 0 && <p className="text-sm text-muted-foreground">No items yet.</p>}
          {items.map((it: any) => (
            <div key={it.id} className="flex items-start justify-between gap-3 border rounded-lg p-3">
              <div className="min-w-0 flex-1">
                <div className="font-medium text-sm truncate">{getEn(it.title)}</div>
                <div className="text-xs text-muted-foreground line-clamp-2">{getEn(it[summaryField])}</div>
                <div className="text-[11px] text-muted-foreground mt-1">{new Date(it.published_at).toLocaleDateString()}</div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <Switch checked={it.is_published} onCheckedChange={() => togglePublish(it)} />
                <Button variant="ghost" size="icon" onClick={() => remove(it.id)}><Trash2 className="h-4 w-4 text-destructive" /></Button>
              </div>
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
}

// ───── Cities ─────
function CitiesEditor() {
  const qc = useQueryClient();
  const { data: items = [] } = useQuery({
    queryKey: ['cms-cities'],
    queryFn: async () => {
      const { data } = await supabase.from('cities').select('*').order('display_order');
      return data || [];
    },
  });
  const [name, setName] = useState('');
  const [region, setRegion] = useState('');

  const add = async () => {
    if (!name.trim()) return;
    const { error } = await supabase.from('cities').insert({ name: name.trim(), region: region.trim() || null, display_order: items.length + 1 });
    if (error) return toast({ title: 'Add failed', description: error.message, variant: 'destructive' });
    setName(''); setRegion('');
    qc.invalidateQueries({ queryKey: ['cms-cities'] });
    qc.invalidateQueries({ queryKey: ['cities-active'] });
  };
  const toggle = async (c: any) => {
    await supabase.from('cities').update({ is_active: !c.is_active }).eq('id', c.id);
    qc.invalidateQueries({ queryKey: ['cms-cities'] });
    qc.invalidateQueries({ queryKey: ['cities-active'] });
  };
  const remove = async (id: string) => {
    if (!confirm('Delete this city?')) return;
    await supabase.from('cities').delete().eq('id', id);
    qc.invalidateQueries({ queryKey: ['cms-cities'] });
    qc.invalidateQueries({ queryKey: ['cities-active'] });
  };

  return (
    <Card>
      <CardHeader><CardTitle>Cities</CardTitle></CardHeader>
      <CardContent className="space-y-4">
        <div className="flex gap-2 flex-wrap">
          <Input className="max-w-[180px]" placeholder="City name" value={name} onChange={e => setName(e.target.value)} />
          <Input className="max-w-[180px]" placeholder="Region (optional)" value={region} onChange={e => setRegion(e.target.value)} />
          <Button onClick={add}><Plus className="h-4 w-4 mr-2" /> Add</Button>
        </div>
        <div className="space-y-2">
          {items.map((c: any) => (
            <div key={c.id} className="flex items-center justify-between border rounded-lg p-2.5">
              <div className="text-sm"><span className="font-medium">{c.name}</span>{c.region && <span className="text-muted-foreground"> · {c.region}</span>}</div>
              <div className="flex items-center gap-2">
                <Switch checked={c.is_active} onCheckedChange={() => toggle(c)} />
                <Button variant="ghost" size="icon" onClick={() => remove(c.id)}><Trash2 className="h-4 w-4 text-destructive" /></Button>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

// ───── Explore Tiles ─────
function TilesEditor() {
  const qc = useQueryClient();
  const { data: items = [] } = useQuery({
    queryKey: ['cms-tiles'],
    queryFn: async () => {
      const { data } = await supabase.from('explore_tiles').select('*').order('display_order');
      return data || [];
    },
  });

  const [draft, setDraft] = useState({ title: '', description: '', icon_name: 'Landmark', link_url: '', background_class: 'bg-amber-50', color_class: 'text-amber-600' });

  const add = async () => {
    if (!draft.title.trim() || !draft.link_url.trim()) return toast({ title: 'Title and link required', variant: 'destructive' });
    const { error } = await supabase.from('explore_tiles').insert({
      title: { en: draft.title },
      description: draft.description ? { en: draft.description } : null,
      icon_name: draft.icon_name,
      link_url: draft.link_url,
      background_class: draft.background_class,
      color_class: draft.color_class,
      display_order: items.length + 1,
    });
    if (error) return toast({ title: 'Add failed', description: error.message, variant: 'destructive' });
    setDraft({ title: '', description: '', icon_name: 'Landmark', link_url: '', background_class: 'bg-amber-50', color_class: 'text-amber-600' });
    qc.invalidateQueries({ queryKey: ['cms-tiles'] });
    qc.invalidateQueries({ queryKey: ['explore-tiles'] });
  };
  const toggle = async (t: any) => {
    await supabase.from('explore_tiles').update({ is_active: !t.is_active }).eq('id', t.id);
    qc.invalidateQueries({ queryKey: ['cms-tiles'] });
    qc.invalidateQueries({ queryKey: ['explore-tiles'] });
  };
  const remove = async (id: string) => {
    if (!confirm('Delete this tile?')) return;
    await supabase.from('explore_tiles').delete().eq('id', id);
    qc.invalidateQueries({ queryKey: ['cms-tiles'] });
    qc.invalidateQueries({ queryKey: ['explore-tiles'] });
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader><CardTitle>Add tile</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <div className="grid md:grid-cols-2 gap-3">
            <div><Label>Title</Label><Input value={draft.title} onChange={e => setDraft({ ...draft, title: e.target.value })} /></div>
            <div><Label>Link URL</Label><Input value={draft.link_url} onChange={e => setDraft({ ...draft, link_url: e.target.value })} placeholder="/projects" /></div>
          </div>
          <div><Label>Description</Label><Input value={draft.description} onChange={e => setDraft({ ...draft, description: e.target.value })} /></div>
          <div className="grid md:grid-cols-3 gap-3">
            <div>
              <Label>Icon</Label>
              <select className="w-full h-10 border rounded-md px-3 text-sm bg-background" value={draft.icon_name} onChange={e => setDraft({ ...draft, icon_name: e.target.value })}>
                {ICON_OPTIONS.map(i => <option key={i} value={i}>{i}</option>)}
              </select>
            </div>
            <div><Label>Background class</Label><Input value={draft.background_class} onChange={e => setDraft({ ...draft, background_class: e.target.value })} /></div>
            <div><Label>Icon color class</Label><Input value={draft.color_class} onChange={e => setDraft({ ...draft, color_class: e.target.value })} /></div>
          </div>
          <Button onClick={add}><Plus className="h-4 w-4 mr-2" /> Add tile</Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle>Existing tiles ({items.length})</CardTitle></CardHeader>
        <CardContent className="space-y-2">
          {items.map((t: any) => (
            <div key={t.id} className="flex items-center justify-between gap-3 border rounded-lg p-3">
              <div className="min-w-0 flex-1">
                <div className="font-medium text-sm">{getEn(t.title)} <span className="text-xs text-muted-foreground">→ {t.link_url}</span></div>
                <div className="text-xs text-muted-foreground">{getEn(t.description)} · icon: {t.icon_name}</div>
              </div>
              <div className="flex items-center gap-2">
                <Switch checked={t.is_active} onCheckedChange={() => toggle(t)} />
                <Button variant="ghost" size="icon" onClick={() => remove(t.id)}><Trash2 className="h-4 w-4 text-destructive" /></Button>
              </div>
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
}

// ───── Partners ─────
function PartnersEditor() {
  const qc = useQueryClient();
  const { data: items = [] } = useQuery({
    queryKey: ['cms-partners'],
    queryFn: async () => {
      const { data } = await supabase.from('landing_partners').select('*').order('display_order');
      return data || [];
    },
  });
  const [draft, setDraft] = useState({ name: '', logo_url: '', link_url: '' });
  const [cropSrc, setCropSrc] = useState<string | null>(null);
  const [cropTarget, setCropTarget] = useState<{ kind: 'draft' } | { kind: 'row'; id: string } | null>(null);
  const [uploading, setUploading] = useState(false);

  const pickFile = (target: { kind: 'draft' } | { kind: 'row'; id: string }) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = () => {
      const file = input.files?.[0];
      if (!file) return;
      const reader = new FileReader();
      reader.onload = () => {
        setCropSrc(reader.result as string);
        setCropTarget(target);
      };
      reader.readAsDataURL(file);
    };
    input.click();
  };

  const handleCropConfirm = async (blob: Blob) => {
    setUploading(true);
    try {
      const url = await uploadPartnerLogo(blob);
      if (cropTarget?.kind === 'draft') {
        setDraft((d) => ({ ...d, logo_url: url }));
      } else if (cropTarget?.kind === 'row') {
        await supabase.from('landing_partners').update({ logo_url: url }).eq('id', cropTarget.id);
        qc.invalidateQueries({ queryKey: ['cms-partners'] });
        qc.invalidateQueries({ queryKey: ['landing-partners'] });
      }
      toast({ title: 'Logo updated' });
      setCropSrc(null);
      setCropTarget(null);
    } catch (e: any) {
      toast({ title: 'Upload failed', description: e.message, variant: 'destructive' });
    } finally {
      setUploading(false);
    }
  };

  const add = async () => {
    if (!draft.name.trim()) return toast({ title: 'Name required', variant: 'destructive' });
    const { error } = await supabase.from('landing_partners').insert({
      name: draft.name.trim(),
      logo_url: draft.logo_url || null,
      link_url: draft.link_url || null,
      display_order: items.length + 1,
    });
    if (error) return toast({ title: 'Add failed', description: error.message, variant: 'destructive' });
    setDraft({ name: '', logo_url: '', link_url: '' });
    qc.invalidateQueries({ queryKey: ['cms-partners'] });
    qc.invalidateQueries({ queryKey: ['landing-partners'] });
  };
  const toggle = async (p: any) => {
    await supabase.from('landing_partners').update({ is_active: !p.is_active }).eq('id', p.id);
    qc.invalidateQueries({ queryKey: ['cms-partners'] });
    qc.invalidateQueries({ queryKey: ['landing-partners'] });
  };
  const remove = async (id: string) => {
    if (!confirm('Delete this partner?')) return;
    await supabase.from('landing_partners').delete().eq('id', id);
    qc.invalidateQueries({ queryKey: ['cms-partners'] });
    qc.invalidateQueries({ queryKey: ['landing-partners'] });
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader><CardTitle>Add partner</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <div className="grid md:grid-cols-3 gap-3">
            <div><Label>Name</Label><Input value={draft.name} onChange={e => setDraft({ ...draft, name: e.target.value })} /></div>
            <div>
              <Label>Logo</Label>
              <div className="flex items-center gap-2">
                {draft.logo_url ? (
                  <img src={draft.logo_url} alt="" className="h-10 w-16 object-contain border rounded bg-white" />
                ) : (
                  <div className="h-10 w-16 border rounded bg-muted" />
                )}
                <Button type="button" variant="outline" size="sm" onClick={() => pickFile({ kind: 'draft' })} disabled={uploading}>
                  {uploading ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Upload className="h-4 w-4 mr-1" />}
                  {draft.logo_url ? 'Replace' : 'Upload'}
                </Button>
              </div>
              <Input className="mt-2" value={draft.logo_url} onChange={e => setDraft({ ...draft, logo_url: e.target.value })} placeholder="…or paste URL" />
            </div>
            <div><Label>Link URL (optional)</Label><Input value={draft.link_url} onChange={e => setDraft({ ...draft, link_url: e.target.value })} placeholder="https://…" /></div>
          </div>
          <Button onClick={add}><Plus className="h-4 w-4 mr-2" /> Add partner</Button>
        </CardContent>
      </Card>
      <Card>
        <CardHeader><CardTitle>Existing partners ({items.length})</CardTitle></CardHeader>
        <CardContent className="space-y-2">
          {items.map((p: any) => (
            <div key={p.id} className="flex items-center justify-between gap-3 border rounded-lg p-3">
              <div className="flex items-center gap-3 min-w-0 flex-1">
                {p.logo_url ? <img src={p.logo_url} alt={p.name} className="h-10 w-16 object-contain bg-white border rounded" /> : <div className="h-10 w-16 bg-muted rounded" />}
                <div className="min-w-0">
                  <div className="font-medium text-sm truncate">{p.name}</div>
                  {p.link_url && <div className="text-xs text-muted-foreground truncate">{p.link_url}</div>}
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <Button variant="outline" size="sm" onClick={() => pickFile({ kind: 'row', id: p.id })} disabled={uploading}>
                  <Pencil className="h-3.5 w-3.5 mr-1" /> {p.logo_url ? 'Recrop' : 'Upload'}
                </Button>
                <Switch checked={p.is_active} onCheckedChange={() => toggle(p)} />
                <Button variant="ghost" size="icon" onClick={() => remove(p.id)}><Trash2 className="h-4 w-4 text-destructive" /></Button>
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      <ImageCropDialog
        open={!!cropSrc}
        imageSrc={cropSrc}
        aspect={3 / 2}
        title="Crop partner logo"
        onCancel={() => { setCropSrc(null); setCropTarget(null); }}
        onConfirm={handleCropConfirm}
      />
    </div>
  );
}

// ───── Partner Stats ─────
function PartnerStatsEditor() {
  const qc = useQueryClient();
  const { data: items = [] } = useQuery({
    queryKey: ['cms-partner-stats'],
    queryFn: async () => {
      const { data } = await supabase.from('landing_partner_stats').select('*').order('display_order');
      return data || [];
    },
  });
  const [draft, setDraft] = useState({ label: '', value: '', icon_name: 'Users' });

  const add = async () => {
    if (!draft.label.trim() || !draft.value.trim()) return toast({ title: 'Label and value required', variant: 'destructive' });
    const { error } = await supabase.from('landing_partner_stats').insert({
      label: draft.label.trim(), value: draft.value.trim(), icon_name: draft.icon_name, display_order: items.length + 1,
    });
    if (error) return toast({ title: 'Add failed', description: error.message, variant: 'destructive' });
    setDraft({ label: '', value: '', icon_name: 'Users' });
    qc.invalidateQueries({ queryKey: ['cms-partner-stats'] });
    qc.invalidateQueries({ queryKey: ['landing-partner-stats'] });
  };
  const toggle = async (s: any) => {
    await supabase.from('landing_partner_stats').update({ is_active: !s.is_active }).eq('id', s.id);
    qc.invalidateQueries({ queryKey: ['cms-partner-stats'] });
    qc.invalidateQueries({ queryKey: ['landing-partner-stats'] });
  };
  const remove = async (id: string) => {
    if (!confirm('Delete this stat?')) return;
    await supabase.from('landing_partner_stats').delete().eq('id', id);
    qc.invalidateQueries({ queryKey: ['cms-partner-stats'] });
    qc.invalidateQueries({ queryKey: ['landing-partner-stats'] });
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader><CardTitle>Add stat</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <div className="grid md:grid-cols-3 gap-3">
            <div><Label>Value</Label><Input value={draft.value} onChange={e => setDraft({ ...draft, value: e.target.value })} placeholder="250+" /></div>
            <div><Label>Label</Label><Input value={draft.label} onChange={e => setDraft({ ...draft, label: e.target.value })} placeholder="Developers" /></div>
            <div>
              <Label>Icon</Label>
              <select className="w-full h-10 border rounded-md px-3 text-sm bg-background" value={draft.icon_name} onChange={e => setDraft({ ...draft, icon_name: e.target.value })}>
                {ICON_OPTIONS.map(i => <option key={i} value={i}>{i}</option>)}
              </select>
            </div>
          </div>
          <Button onClick={add}><Plus className="h-4 w-4 mr-2" /> Add stat</Button>
        </CardContent>
      </Card>
      <Card>
        <CardHeader><CardTitle>Existing stats ({items.length})</CardTitle></CardHeader>
        <CardContent className="space-y-2">
          {items.map((s: any) => (
            <div key={s.id} className="flex items-center justify-between gap-3 border rounded-lg p-3">
              <div className="text-sm"><span className="font-semibold">{s.value}</span> · {s.label} <span className="text-xs text-muted-foreground">({s.icon_name})</span></div>
              <div className="flex items-center gap-2">
                <Switch checked={s.is_active} onCheckedChange={() => toggle(s)} />
                <Button variant="ghost" size="icon" onClick={() => remove(s.id)}><Trash2 className="h-4 w-4 text-destructive" /></Button>
              </div>
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
}

export function LandingCMS() {
  const { hasRole } = useAuth();
  const allowed = hasRole('superadmin') || hasRole('marketing_manager') || hasRole('marketing_team_leader') || hasRole('marketing_agent');

  if (!allowed) {
    return (
      <Card><CardContent className="py-10 text-center text-sm text-muted-foreground">You don't have access to the Landing CMS.</CardContent></Card>
    );
  }

  return (
    <div className="space-y-4 max-w-5xl">
      <div>
        <h1 className="text-2xl font-bold">Landing Page CMS</h1>
        <p className="text-sm text-muted-foreground">Manage homepage content: hero, news, blog, cities, and explore tiles.</p>
      </div>
      <Tabs defaultValue="hero">
        <TabsList className="flex-wrap h-auto">
          <TabsTrigger value="hero">Hero</TabsTrigger>
          <TabsTrigger value="news">News</TabsTrigger>
          <TabsTrigger value="blog">Blog</TabsTrigger>
          <TabsTrigger value="cities">Cities</TabsTrigger>
          <TabsTrigger value="tiles">Explore Tiles</TabsTrigger>
          <TabsTrigger value="partners">Partners</TabsTrigger>
          <TabsTrigger value="stats">Partner Stats</TabsTrigger>
        </TabsList>
        <TabsContent value="hero" className="mt-4"><HeroEditor /></TabsContent>
        <TabsContent value="news" className="mt-4"><ArticleEditor table="news_articles" hasSlug={false} /></TabsContent>
        <TabsContent value="blog" className="mt-4"><ArticleEditor table="blog_posts" hasSlug /></TabsContent>
        <TabsContent value="cities" className="mt-4"><CitiesEditor /></TabsContent>
        <TabsContent value="tiles" className="mt-4"><TilesEditor /></TabsContent>
        <TabsContent value="partners" className="mt-4"><PartnersEditor /></TabsContent>
        <TabsContent value="stats" className="mt-4"><PartnerStatsEditor /></TabsContent>
      </Tabs>
    </div>
  );
}
